import { CELL_WIDTH } from '@components/export/export.config';
import * as GC from '@grapecity/spread-sheets';
import { IProduct, IProductGroup } from '@models/product';
import { emptyItemRow, IProductCPSummaryItem, IProductSummaryResponse } from '@models/reports/current-month-summary';
import { round, weightedAverage } from '@shared/numbers';
import { borders, colours, CURRENCY_FORMATTER, DASH, FONT_NAME, FONT_SIZE, PERCENTAGE_FORMATTER } from '@shared/spread/spread-settings';
import { getDateForDisplay } from '../helpers';

const HEADERS = ['AVG BALANCE', 'END BALANCE', 'COUPON', 'WEIGHT OF TOTAL', 'WEIGHTED AVG LIFE', '# OF ACCOUNTS'];
const TOTAL_COLUMNS = HEADERS.length + 1; // The "+1" is for the Product Group Name
const HEADERS_ROW_HEIGHT = 40;

const getFont = (option: 'small' | 'medium' | 'large' | 'x-large', bold: boolean = false) => {
  let multiplier: number;
  switch (option) {
    case 'small':
      multiplier = 0.8;
      break;
    case 'medium':
      multiplier = 1;
      break;
    case 'large':
      multiplier = 1.1;
      break;
    case 'x-large':
      multiplier = 1.2;
      break;
  }

  return `${bold ? 'bold ' : ''}${Math.round(Number(FONT_SIZE) * multiplier)}pt ${FONT_NAME}`;
};

export const createProductSummaryExport = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  lastObservationDate: Date,
  productData: any[],
  totals: IProductCPSummaryItem
): number => {
  // Last Observation Date (OBSDATE)
  rowIndex = addlastObservationDate(sheet, rowIndex, colIndex, lastObservationDate);
  // Summary Details By ProductGroup
  rowIndex = addSummaryDetailsByProductGroup(sheet, rowIndex, colIndex, totals, productData);
  // Grand Total
  rowIndex = addGrandTotal(sheet, rowIndex, colIndex, totals);

  sheet.zoom(0.65);

  return rowIndex;
};

const addlastObservationDate = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  lastObservationDate: Date
): number => {
  const takenRows = 2;

  sheet
    .getCell(rowIndex, colIndex)
    .text('Last\nObservation')
    .font(getFont('x-large', true));
  sheet.getCell(rowIndex, colIndex + 1).text(getDateForDisplay(lastObservationDate));

  // Styling
  const asOfDateRange = sheet.getRange(rowIndex, colIndex, 1, takenRows);
  asOfDateRange
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .backColor(colours.LIGHT_BLUE);
  asOfDateRange.setBorder(borders.BLACK_MEDIUM, { outline: true });

  rowIndex += 2;

  return rowIndex;
};

function addSummaryDetailsByProductGroup(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  totals: IProductCPSummaryItem,
  productData: any[]
): number {
  productData.forEach(entry => {
    rowIndex = createProductGroupEntry(sheet, rowIndex, colIndex, entry, totals);
    rowIndex += 2; // Margin btw them
  });

  return rowIndex;
}

// GRAND TOTAL
// -----------
function addGrandTotal(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number, totals: IProductCPSummaryItem): number {
  const grandTotalDisplayedValues = [
    'Grand Total', // Row header
    totals.AvgBalance,
    totals.EndBalance,
    totals.CouponRate,
    1, // The global total is 100% of the percentage of the totals as is the sum of every total
    round(totals.AverageLife, 2),
    totals.NumberOfAccts
  ];
  grandTotalDisplayedValues.forEach((gtVal, ind) => sheet.getCell(rowIndex, colIndex + ind).value(gtVal));

  // STYLING
  createRowStyling(sheet, rowIndex, colIndex);
  const grandTotalsRange = sheet.getRange(rowIndex, colIndex, 1, TOTAL_COLUMNS);
  grandTotalsRange
    .font(getFont('x-large', true))
    .backColor(colours.LIGHT_GREY)
    .setBorder(borders.BLACK_MEDIUM, { top: true });
  sheet.setRowHeight(grandTotalsRange.row, HEADERS_ROW_HEIGHT);

  rowIndex++;
  return rowIndex;
}

function populateGroupHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number, productData: any): number {
  // Product Name
  sheet.getCell(rowIndex, colIndex).text(productData.name);

  // Column Header's Names
  HEADERS.forEach((header, ind) => sheet.getCell(rowIndex, colIndex + 1 + ind).text(header));

  // Styling
  const productHeaderRange = sheet.getRange(rowIndex, colIndex, 1, TOTAL_COLUMNS);
  productHeaderRange
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .font(getFont('x-large', true))
    // .backColor(colours.LIGHT_GREY)
    .setBorder(borders.BLACK_MEDIUM, { bottom: true });
  sheet.getCell(rowIndex, colIndex).hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  // Set column width and row height
  sheet.setRowHeight(productHeaderRange.row, HEADERS_ROW_HEIGHT);

  for (let i = 0; i < TOTAL_COLUMNS; i++) {
    sheet.setColumnWidth(colIndex + i, CELL_WIDTH * 3); // TODO: Use CELL_WIDTH for all the columns and CELL_HEIGHT for all the rows
  }

  rowIndex++;
  return rowIndex;
}

function createProductGroupEntry(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  colIndex: number,
  productData: any,
  totals: IProductCPSummaryItem
): number {
  // PRODUCT HEADERS
  rowIndex = populateGroupHeaders(sheet, rowIndex, colIndex, productData);

  // PRODUCT-BASED VALUES
  productData.data.forEach(pd => {
    const isValidValue = val => !(val === null || val === undefined || isNaN(val));

    sheet.getCell(rowIndex, colIndex).text(pd.ProductName);
    sheet.getCell(rowIndex, colIndex + 1).value(isValidValue(pd.AvgBalance) ? pd.AvgBalance : DASH);
    sheet.getCell(rowIndex, colIndex + 2).value(isValidValue(pd.EndBalance) ? pd.EndBalance : DASH);
    sheet.getCell(rowIndex, colIndex + 3).value(isValidValue(pd.CouponRate) ? pd.CouponRate : DASH);
    sheet
      .getCell(rowIndex, colIndex + 4)
      .value(
        isValidValue(totals.AvgBalance) && isValidValue(pd.AvgBalance) && totals.AvgBalance !== 0 && pd.AvgBalance !== 0
          ? pd.AvgBalance / totals.AvgBalance
          : DASH
      );
    sheet.getCell(rowIndex, colIndex + 5).value(isValidValue(pd.AverageLife) ? pd.AverageLife : DASH);
    sheet.getCell(rowIndex, colIndex + 6).value(isValidValue(pd.NumberOfAccts) ? pd.NumberOfAccts : DASH);

    createRowStyling(sheet, rowIndex, colIndex);
    rowIndex++;
  });

  // PRODUCT GROUP TOTAL

  const displayedValues = [
    'Total', // Row header
    productData.groupData.AvgBalance,
    productData.groupData.EndBalance,
    productData.groupData.CouponRate,
    totals.AvgBalance && productData.groupData.AvgBalance ? productData.groupData.AvgBalance / totals.AvgBalance : DASH,
    productData.groupData.AverageLife,
    productData.groupData.NumberOfAccts
  ];
  displayedValues.forEach((val, ind) => sheet.getCell(rowIndex, colIndex + ind).value(val));

  const groupTotalRange = sheet.getRange(rowIndex, colIndex, 1, TOTAL_COLUMNS);
  groupTotalRange
    .backColor(colours.LIGHT_GREY)
    .font(getFont('medium', true))
    .hAlign(GC.Spread.Sheets.HorizontalAlign.right)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .setBorder(borders.BLACK_THIN, { top: true });

  sheet.getCell(rowIndex, colIndex).hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  // STYLING
  createRowStyling(sheet, rowIndex, colIndex);
  // const entireRange = sheet.getRange(rowIndex, colIndex, 1, TOTAL_COLUMNS);
  // entireRange.setBorder(borders.BLACK_MEDIUM, { top: true, bottom: true });

  rowIndex++;

  return rowIndex;
}

function createRowStyling(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, colIndex: number) {
  const entireRow = sheet.getRange(rowIndex, colIndex, 1, TOTAL_COLUMNS);

  sheet.getCell(rowIndex, colIndex + 1).formatter(CURRENCY_FORMATTER);
  sheet.getCell(rowIndex, colIndex + 2).formatter(CURRENCY_FORMATTER);
  sheet.getCell(rowIndex, colIndex + 3).formatter(PERCENTAGE_FORMATTER);
  sheet.getCell(rowIndex, colIndex + 4).formatter(PERCENTAGE_FORMATTER);
  entireRow.hAlign(GC.Spread.Sheets.HorizontalAlign.right).vAlign(GC.Spread.Sheets.VerticalAlign.center);

  sheet.getCell(rowIndex, colIndex).hAlign(GC.Spread.Sheets.HorizontalAlign.left);
}

export const buildProductSummaryData = async (
  products: IProduct[],
  productGroups: IProductGroup[],
  productSummaries: IProductSummaryResponse[]
): Promise<[any[], IProductCPSummaryItem]> => {
  const extendedProducts = products.map(product => {
    let summaryDetailsForProduct = emptyItemRow();

    const summaryDetailsFromAPI = productSummaries.find(item => item.ProductID === product.ProductId);
    if (summaryDetailsFromAPI) {
      summaryDetailsForProduct = { ...summaryDetailsForProduct, ...summaryDetailsFromAPI };
    }

    product = { ...product, ...summaryDetailsForProduct };
    return product;
  });

  const productData = [];
  productGroups.forEach(group => {
    const sameGroupProducts: IProduct[] = extendedProducts.filter(pr => pr.ProductGroupId === group.ProductGroupId);
    if (sameGroupProducts.length > 0) {
      const obj = {
        id: group.ProductGroupId,
        name: group.GroupName,
        data: sameGroupProducts,
        groupData: calculateTotalsForGroup(sameGroupProducts)
      } as any;
      productData.push(obj);
    }
  });

  const totals = calculateTotalsForGroup(productData.map(({ groupData }) => groupData));

  return [productData, totals];
};

function calculateTotalsForGroup(products: any[]) {
  const groupTotals = emptyItemRow();

  // Avg Balance  => SUM (Product 1 Avg, Product 5 Avg)
  groupTotals.AvgBalance = getSumOfGroup(products, 'AvgBalance');

  // End Balance => SUM (Product 1 End, Product 5 End)
  groupTotals.EndBalance = getSumOfGroup(products, 'EndBalance');

  // Coupon Rate => SUMPRODUCT (Product 1 Avg: Product 5 Avg, Coupon 1: Coupon 5)/ SUM (Product 1 Avg, Product 5 Avg)
  // Weighted Avg Life => SUMPRODUCT (Product 1 Avg: Product 5 Avg, WAL 1: WAL 5)/ SUM (Product 1 Avg, Product 5 Avg)
  if (groupTotals.AvgBalance) {
    groupTotals.CouponRate = getWeightedAvgFromGroup(products, 'CouponRate');
    const averageLife = getWeightedAvgFromGroup(products, 'AverageLife');
    // @ts-ignore
    groupTotals.AverageLife = averageLife !== null ? round(averageLife, 2) : DASH;
  }

  // # of Accounts => SUM (# of Accounts for product 1, # of accounts for product 5)
  groupTotals.NumberOfAccts = getSumOfGroup(products, 'NumberOfAccts');
  return groupTotals;
}

function getSumOfGroup(products: any[], column: 'AvgBalance' | 'EndBalance' | 'NumberOfAccts' | 'WeightOfTotal') {
  return products.reduce((total: number, current) => {
    if (total === null && current[column] !== null) {
      return current[column];
    } else if (total !== null && current[column] !== null) {
      return total + current[column];
    }
    return total;
  }, null);
}

function getWeightedAvgFromGroup(products: any[], column: 'CouponRate' | 'AverageLife') {
  const series = [];
  for (const product of products) {
    if (product[column] !== null) {
      const columnValue = product[column] === null ? 0 : product[column];
      series.push([columnValue, product.AvgBalance]);
    }
  }
  if (series.length > 0) {
    return weightedAverage(series);
  } else {
    return null;
  }
}
